import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { IActiveParsing, PARSE_STATE } from '../../../../interfaces/cv-parse';
import { RxState } from '@rx-angular/state';
import { CvParseFacadeService } from '../../../../services/facades/cv-parse.facade.service';
import { filter, Observable, Subscription, take } from 'rxjs';
import { ConsultantsFeatures } from '@my7n/features';
import { AuthorizationService } from '../../../../services/authorization.service';
import { ConfirmDialogComponent, ConfirmDialogComponentDefaultConfig, MatDialogService } from '@my7n/ui';
import { MatDialogRef } from '@angular/material/dialog';

export interface ICvParseRibbonLocalState {
  activeParsing: IActiveParsing;
  cancelParsing: {
    error: boolean;
    loading: boolean;
  };
  parsingResult: {
    cvId: number;
    error: boolean;
    loading: boolean;
  };
  parsingState: PARSE_STATE;
  ribbon: {
    folded: boolean;
    show: boolean;
  };
}

export const initialState: ICvParseRibbonLocalState = {
  activeParsing: {
    requestId: null,
    cvId: null,
    loading: false,
    error: false,
  },
  cancelParsing: {
    error: false,
    loading: false,
  },
  parsingResult: {
    cvId: null,
    error: false,
    loading: false,
  },
  parsingState: null,
  ribbon: {
    folded: false,
    show: false,
  },
};

@Component({
  selector: 'cv-parse-ribbon',
  templateUrl: './cv-parse-ribbon.component.html',
  styleUrls: ['./cv-parse-ribbon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RxState],
  standalone: false,
})
export class CvParseRibbonComponent implements OnInit, OnDestroy {
  readonly state$ = this.state.select();

  subscriptions$ = new Subscription();
  hasAccessToConsultantProfile$: Observable<boolean>;
  parsedCvUrl: string;
  parseRemoveConfirmDialogRef: MatDialogRef<ConfirmDialogComponent>;

  constructor(
    private state: RxState<ICvParseRibbonLocalState>,
    private cvParseFacadeService: CvParseFacadeService,
    private authorizationService: AuthorizationService,
    private dialog: MatDialogService,
  ) {
    this.state.set({ ...initialState });
    this.hasAccessToConsultantProfile$ = this.authorizationService.can(this.consultantItemCvViewFeature);
  }

  PARSING_STATE = PARSE_STATE;

  get consultantItemCvViewFeature() {
    return ConsultantsFeatures.ItemCvView;
  }

  ngOnInit() {
    this.initLocalStateConnections();
    this.parsingStateChangeHandler();
    this.subscriptions$.add(this.hasAccessToConsultantProfile$.subscribe(
      (hasAccess) => {
        if (hasAccess) {
          this.parsedCvUrl = '/agent/consultant/cv/';
        } else {
          this.parsedCvUrl = '/cv/';
        }
      }
    ));
  }

  initLocalStateConnections() {
    this.state.connect('activeParsing', this.cvParseFacadeService.activeParsingStatus$);
    this.state.connect('parsingState', this.cvParseFacadeService.parsingState$);
    this.state.connect('cancelParsing', this.cvParseFacadeService.cancelParsingStatus$);
    this.state.connect('parsingResult', this.cvParseFacadeService.parsingResultStatus$);
    this.state.connect('ribbon', this.cvParseFacadeService.ribbonState$);
  }

  foldToggle() {
    this.cvParseFacadeService.updateRibbonFoldedState(!this.state.get().ribbon.folded);
  }

  cancelParsing() {
    this.parseRemoveConfirmDialogRef = this.dialog.open(ConfirmDialogComponent, {
      ...ConfirmDialogComponentDefaultConfig,
      data: {
        dialogHTML: `Are you sure you want to cancel Parsing CV?`,
        headerText: 'Cancel CV',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }
    });
    
    this.subscriptions$.add(
      this.parseRemoveConfirmDialogRef.afterClosed()
        .pipe(
          take(1),
          filter((result: boolean) => result === true)
        )
        .subscribe(() => {
          this.cvParseFacadeService.cancelActiveParsing(this.state.get().activeParsing.cvId);
        })
    );
  }
  
  resetParsing() {
    this.cvParseFacadeService.resetParsingState();
  }

  private parsingStateChangeHandler() {
    this.subscriptions$.add(
      this.state
        .select('parsingState')
        .pipe(filter((parsingState) => parsingState !== null))
        .subscribe(() => {
          // Set show immediately
          this.cvParseFacadeService.updateRibbonShowState(true);

          // Set folded after 50ms delay for smooth animation
          setTimeout(() => {
            this.cvParseFacadeService.updateRibbonFoldedState(false);
          }, 50);
        })
    );
  }

  ngOnDestroy() {
    this.subscriptions$.unsubscribe();
  }
}
